import { diffHydrateFiles } from '~/utils/uploaderAdapter';
import type { HydrateFile } from '@magicdoorinc/ui';
import type {
  BusinessInfoDraftDto,
  BusinessInfoDraftPrincipalDto,
  BusinessInfoDraftPrincipalRequestDto,
  DriverLicenseDto,
  LegalEntityType,
  PatchBusinessInfoDraftRequestDto,
} from '~/swagger/companies/Api';
import type { BusinessInfoHydrateFile } from '~/utils/companyBusinessDocuments';

export interface BusinessInfoFileFormValue {
  obligationKey: string;
  label?: string;
  description?: string;
  files?: HydrateFile[];
}

export interface PrincipalFileFormValue {
  obligationKey: string;
  label?: string;
  description?: string;
  files?: HydrateFile[];
}

export interface PrincipalFormValue extends Omit<Partial<BusinessInfoDraftPrincipalRequestDto>, 'address' | 'driverLicense'> {
  id?: string;
  clientKey?: string;
  address?: BusinessInfoDraftPrincipalRequestDto['address'] | BusinessInfoDraftPrincipalDto['address'];
  driversLicense?: { number?: string; state?: string };
  principalFiles?: PrincipalFileFormValue[];
}

export type CompanyBusinessInfoFormPayload = Partial<
  Omit<PatchBusinessInfoDraftRequestDto, 'businessAddress'> & {
    businessAddress?: PatchBusinessInfoDraftRequestDto['businessAddress'];
    businessInfoFiles?: BusinessInfoFileFormValue[];
    principals?: PrincipalFormValue[];
  }
>;

export type DocumentAction = {
  rawFile: File;
  obligationKey: string;
  principalIndex?: number;
  principalClientKey?: string;
};

export type DocumentDeleteAction = {
  documentId: string;
  obligationKey: string;
};

export type PrincipalMutation = {
  type: 'add' | 'patch' | 'delete';
  principalId?: string;
  clientKey?: string;
  index: number;
  payload?: BusinessInfoDraftPrincipalRequestDto;
};

const cloneHydrateFiles = (files?: HydrateFile[]) => files?.map((file) => ({ ...file }));
const MASK_CHARACTER = '•';

const isMaskedValue = (value: unknown) => typeof value === 'string' && value.includes(MASK_CHARACTER);

const unmaskSensitiveValue = (value: string | undefined) => {
  if (isMaskedValue(value)) return undefined;
  return value;
};

const cloneBusinessInfoFiles = (fields?: BusinessInfoFileFormValue[]) =>
  fields?.map((field) => ({
    ...field,
    files: cloneHydrateFiles(field.files),
  }));

const clonePrincipalFiles = (fields?: PrincipalFileFormValue[]) =>
  fields?.map((field) => ({
    ...field,
    files: cloneHydrateFiles(field.files),
  }));

export const snapshotCompanyBusinessInfoFormPayload = (payload: CompanyBusinessInfoFormPayload): CompanyBusinessInfoFormPayload => ({
  legalName: payload.legalName,
  ein: payload.ein,
  legalEntityType: payload.legalEntityType,
  mccCode: payload.mccCode,
  summary: payload.summary,
  startDate: payload.startDate,
  businessAddress: payload.businessAddress ? { ...payload.businessAddress } : undefined,
  businessInfoFiles: cloneBusinessInfoFiles(payload.businessInfoFiles),
  principals: payload.principals?.map((principal) => ({
    ...principal,
    address: principal.address ? { ...principal.address } : undefined,
    driversLicense: principal.driversLicense ? { ...principal.driversLicense } : undefined,
    principalFiles: clonePrincipalFiles(principal.principalFiles),
  })),
});

export const hasCompanyBusinessInfoFormContent = (payload: CompanyBusinessInfoFormPayload) => {
  if (payload.legalName?.trim()) return true;
  if (payload.ein?.trim()) return true;
  if (payload.legalEntityType) return true;
  if (payload.startDate) return true;
  if (payload.businessAddress?.streetAddress1?.trim()) return true;

  const principals = payload.principals ?? [];
  if (
    principals.some(
      (principal) =>
        principal.firstName?.trim() ||
        principal.lastName?.trim() ||
        principal.email?.trim() ||
        principal.phone?.trim() ||
        principal.ssn?.trim() ||
        principal.dateOfBirth?.trim() ||
        principal.address?.streetAddress1?.trim() ||
        principal.driversLicense?.number?.trim() ||
        principal.driversLicense?.state?.trim()
    )
  ) {
    return true;
  }

  const hasNewBusinessFiles = payload.businessInfoFiles?.some((field) =>
    field.files?.some((file) => file.rawFile && file.deleted !== 'deleted')
  );
  if (hasNewBusinessFiles) return true;

  const hasNewPrincipalFiles = principals.some((principal) =>
    principal.principalFiles?.some((field) => field.files?.some((file) => file.rawFile && file.deleted !== 'deleted'))
  );
  return Boolean(hasNewPrincipalFiles);
};

const toDriverLicenseRequest = (driversLicense?: PrincipalFormValue['driversLicense']): DriverLicenseDto | undefined => {
  if (!driversLicense) return undefined;

  const number = unmaskSensitiveValue(driversLicense.number?.trim());
  const state = driversLicense.state?.trim();
  if (!number && !state) return undefined;

  const result: DriverLicenseDto = {};
  if (number) result.number = number;
  if (state) result.state = state;
  return result;
};

const toPrincipalRequest = (principal: PrincipalFormValue): BusinessInfoDraftPrincipalRequestDto => {
  const result: BusinessInfoDraftPrincipalRequestDto = {
    firstName: principal.firstName?.trim(),
    lastName: principal.lastName?.trim(),
    email: unmaskSensitiveValue(principal.email)?.trim(),
    phone: unmaskSensitiveValue(principal.phone)?.trim(),
    ssn: unmaskSensitiveValue(principal.ssn)?.trim(),
    type: principal.type,
    ownership: principal.ownership,
    address: principal.address as BusinessInfoDraftPrincipalRequestDto['address'],
    driverLicense: toDriverLicenseRequest(principal.driversLicense),
  };

  const dateOfBirth = unmaskSensitiveValue(principal.dateOfBirth?.trim());
  if (dateOfBirth) {
    result.dateOfBirth = dateOfBirth;
  }

  return result;
};

export type SaveDraftDefaults = {
  mccCode: string;
  summary: string;
};

const isMissingOptionalString = (value: string | undefined) => typeof value !== 'string' || value.trim() === '';

const snapshotDraftBusinessFields = (draft: BusinessInfoDraftDto) => ({
  legalName: draft.legalName,
  ein: draft.ein,
  legalEntityType: draft.legalEntityType,
  mccCode: draft.mccCode,
  summary: draft.summary,
  startDate: draft.startDate,
  businessAddress: draft.businessAddress,
});

const appendMissingPatchDefaults = (
  draft: BusinessInfoDraftDto,
  patch: PatchBusinessInfoDraftRequestDto,
  defaults: SaveDraftDefaults
): PatchBusinessInfoDraftRequestDto => {
  const result = { ...patch };

  if (isMissingOptionalString(draft.mccCode)) {
    result.mccCode = defaults.mccCode;
  }
  if (isMissingOptionalString(draft.summary)) {
    result.summary = defaults.summary;
  }

  return result;
};

const valuesEqual = (left: unknown, right: unknown) => JSON.stringify(left ?? null) === JSON.stringify(right ?? null);

export const buildPatchDraftPayloadIfChanged = (
  draft: BusinessInfoDraftDto,
  payload: CompanyBusinessInfoFormPayload,
  defaults: SaveDraftDefaults
): PatchBusinessInfoDraftRequestDto | null => {
  const next = buildPatchDraftPayload(payload);
  const current = snapshotDraftBusinessFields(draft);
  const changed: PatchBusinessInfoDraftRequestDto = {};

  (Object.keys(next) as (keyof PatchBusinessInfoDraftRequestDto)[]).forEach((key) => {
    const nextValue = next[key];
    if (nextValue === undefined) return;
    const currentValue = current[key as keyof typeof current];
    if (!valuesEqual(nextValue, currentValue)) {
      Object.assign(changed, { [key]: nextValue });
    }
  });

  const withDefaults = appendMissingPatchDefaults(draft, changed, defaults);
  return Object.keys(withDefaults).length > 0 ? withDefaults : null;
};

const draftPrincipalToFormValue = (draftPrincipal: BusinessInfoDraftPrincipalDto): PrincipalFormValue => ({
  firstName: draftPrincipal.firstName,
  lastName: draftPrincipal.lastName,
  email: draftPrincipal.email,
  phone: draftPrincipal.phone,
  ssn: draftPrincipal.ssn,
  dateOfBirth: draftPrincipal.dateOfBirth,
  type: draftPrincipal.type,
  ownership: draftPrincipal.ownership,
  address: draftPrincipal.address,
  driversLicense: {
    number: draftPrincipal.driverLicense?.number ?? '',
    state: draftPrincipal.driverLicense?.state ?? '',
  },
});

const isPrincipalUnchanged = (principal: PrincipalFormValue, draftPrincipal: BusinessInfoDraftPrincipalDto) =>
  valuesEqual(toPrincipalRequest(principal), toPrincipalRequest(draftPrincipalToFormValue(draftPrincipal)));

const appendFileDiffActions = (
  actions: DocumentAction[],
  files: HydrateFile[] | undefined,
  obligationKey: string,
  principalIndex?: number,
  principalClientKey?: string
) => {
  const diff = diffHydrateFiles(files ?? []);

  diff.newFiles.forEach((file) => {
    if (file.rawFile) {
      actions.push({ rawFile: file.rawFile, obligationKey, principalIndex, principalClientKey });
    }
  });
};

export const collectDocumentActions = (payload: CompanyBusinessInfoFormPayload): DocumentAction[] => {
  const actions: DocumentAction[] = [];

  payload.businessInfoFiles?.forEach((fileField) => {
    appendFileDiffActions(actions, fileField.files, fileField.obligationKey);
  });

  payload.principals?.forEach((principal, principalIndex) => {
    principal.principalFiles?.forEach(({ obligationKey, files }) => {
      appendFileDiffActions(actions, files, obligationKey, principalIndex, principal.clientKey);
    });
  });

  return actions;
};

export const collectDocumentDeleteActions = (payload: CompanyBusinessInfoFormPayload): DocumentDeleteAction[] => {
  const actions: DocumentDeleteAction[] = [];

  const collectDeletes = (files: HydrateFile[] | undefined, obligationKey: string) => {
    diffHydrateFiles(files ?? []).deletedFileIds.forEach((deletedId) => {
      const file = files?.find((entry) => entry.idOrUrl === deletedId || (entry as BusinessInfoHydrateFile).documentId === deletedId);
      const documentId =
        (file as BusinessInfoHydrateFile | undefined)?.documentId ??
        (deletedId.startsWith('document:') ? deletedId.slice('document:'.length) : deletedId);
      actions.push({ documentId, obligationKey });
    });
  };

  payload.businessInfoFiles?.forEach((fileField) => collectDeletes(fileField.files, fileField.obligationKey));

  payload.principals?.forEach((principal) => {
    principal.principalFiles?.forEach(({ obligationKey, files }) => collectDeletes(files, obligationKey));
  });

  return actions;
};

export const buildPatchDraftPayload = (payload: CompanyBusinessInfoFormPayload): PatchBusinessInfoDraftRequestDto => {
  const result: PatchBusinessInfoDraftRequestDto = {};

  if (payload.legalName !== undefined) result.legalName = payload.legalName.trim();
  if (payload.ein !== undefined && !isMaskedValue(payload.ein)) result.ein = payload.ein.trim();
  if (payload.legalEntityType !== undefined) result.legalEntityType = payload.legalEntityType as LegalEntityType;
  if (payload.mccCode !== undefined) result.mccCode = payload.mccCode;
  if (payload.summary !== undefined) result.summary = payload.summary;
  if (payload.startDate !== undefined) result.startDate = payload.startDate;
  if (payload.businessAddress !== undefined) result.businessAddress = payload.businessAddress;

  return result;
};

export const buildPrincipalMutations = (
  formPrincipals: PrincipalFormValue[] | undefined,
  draftPrincipals: BusinessInfoDraftPrincipalDto[] | undefined
): PrincipalMutation[] => {
  const mutations: PrincipalMutation[] = [];
  const formList = formPrincipals ?? [];
  const draftList = draftPrincipals ?? [];
  const draftIds = new Set(draftList.map((p) => p.id).filter(Boolean));

  formList.forEach((principal, index) => {
    if (principal.id && draftIds.has(principal.id)) {
      const draftPrincipal = draftList.find((entry) => entry.id === principal.id);
      if (draftPrincipal && !isPrincipalUnchanged(principal, draftPrincipal)) {
        mutations.push({
          type: 'patch',
          principalId: principal.id,
          clientKey: principal.clientKey,
          index,
          payload: toPrincipalRequest(principal),
        });
      }
      return;
    }

    const draftPrincipalAtIndex = draftList[index];
    const draftPrincipalIdAtIndex = draftPrincipalAtIndex?.id;
    const isDraftPrincipalUnclaimed =
      draftPrincipalIdAtIndex !== undefined && !formList.some((entry) => entry.id === draftPrincipalIdAtIndex);

    if (!principal.id && isDraftPrincipalUnclaimed && isPrincipalUnchanged(principal, draftPrincipalAtIndex)) {
      return;
    }

    if (!principal.id) {
      mutations.push({ type: 'add', clientKey: principal.clientKey, index, payload: toPrincipalRequest(principal) });
    }
  });

  draftList.forEach((draftPrincipal) => {
    if (draftPrincipal.id && !formList.some((p) => p.id === draftPrincipal.id)) {
      mutations.push({ type: 'delete', principalId: draftPrincipal.id, index: -1 });
    }
  });

  return mutations;
};

export const buildPrincipalIndexToIdMap = (
  formPrincipals: PrincipalFormValue[] | undefined,
  draftPrincipals: BusinessInfoDraftPrincipalDto[] | undefined
): Record<number, string> => {
  const map: Record<number, string> = {};
  const formList = formPrincipals ?? [];
  const draftList = draftPrincipals ?? [];
  const claimedDraftIds = new Set<string>();

  formList.forEach((principal, index) => {
    if (principal.id) {
      map[index] = principal.id;
      claimedDraftIds.add(principal.id);
      return;
    }

    const draftPrincipalAtIndex = draftList[index];
    const draftPrincipalIdAtIndex = draftPrincipalAtIndex?.id;

    const matchedDraftPrincipal = draftList.find(
      (draftPrincipal) => draftPrincipal.id && !claimedDraftIds.has(draftPrincipal.id) && isPrincipalUnchanged(principal, draftPrincipal)
    );

    if (matchedDraftPrincipal?.id) {
      map[index] = matchedDraftPrincipal.id;
      claimedDraftIds.add(matchedDraftPrincipal.id);
      return;
    }

    if (draftPrincipalIdAtIndex && !claimedDraftIds.has(draftPrincipalIdAtIndex)) {
      map[index] = draftPrincipalIdAtIndex;
      claimedDraftIds.add(draftPrincipalIdAtIndex);
    }
  });

  return map;
};

export const buildPrincipalClientKeyToIdMap = (
  formPrincipals: PrincipalFormValue[] | undefined,
  draftPrincipals: BusinessInfoDraftPrincipalDto[] | undefined
): Record<string, string> => {
  const map: Record<string, string> = {};
  const formList = formPrincipals ?? [];
  const draftList = draftPrincipals ?? [];
  const claimedDraftIds = new Set<string>();

  formList.forEach((principal, index) => {
    const clientKey = principal.clientKey;
    if (!clientKey) return;

    if (principal.id) {
      map[clientKey] = principal.id;
      claimedDraftIds.add(principal.id);
      return;
    }

    const matchedDraftPrincipal = draftList.find(
      (draftPrincipal) => draftPrincipal.id && !claimedDraftIds.has(draftPrincipal.id) && isPrincipalUnchanged(principal, draftPrincipal)
    );
    if (matchedDraftPrincipal?.id) {
      map[clientKey] = matchedDraftPrincipal.id;
      claimedDraftIds.add(matchedDraftPrincipal.id);
      return;
    }

    const draftPrincipalAtIndex = draftList[index];
    const draftPrincipalIdAtIndex = draftPrincipalAtIndex?.id;
    if (draftPrincipalIdAtIndex && !claimedDraftIds.has(draftPrincipalIdAtIndex)) {
      map[clientKey] = draftPrincipalIdAtIndex;
      claimedDraftIds.add(draftPrincipalIdAtIndex);
    }
  });

  return map;
};
